import { useCallback, useEffect, useState } from "react";
import {
  AppLayout,
  NotificationMessage,
  NotificationMessageKind,
  addNotification,
  notificationService,
  useGame,
  useLayout,
} from "core";
import { useDispatch } from "react-redux";
import {
  LinkedAccountType,
  NotificationType,
  RemoteNotificationTrigger,
} from "@blacknut/javascript-sdk/dist";
import { useErrorHandling } from "src/utils/V2ErrorHandler";
import { logD } from "@blacknut/logging/dist";
import { LOGGING_TAG } from "src/utils/Utils";
import { useLinkedAccounts } from "src/utils/useLinkedAccounts";
import { useAppNavigation } from "./Navigation/useAppNavigation";
import { useWebAppURL } from "./useWebAppURL";

export interface useLinkAccountActivateGame {
  gameId: string;
  provider: string;
  origin?: string;
  onSuccess?: () => void;
}

export const useLinkAccountActivateGame = (props: useLinkAccountActivateGame) => {
  const { gameId, provider, origin, onSuccess: onSuccessProp } = props;
  const { create: createWebAppURL } = useWebAppURL();
  const [url, setUrl] = useState<string>();
  const {
    addToBonusGame,
    dismissUbisoftPYGActivateError,
    ubisoftPYGActivateError,
    pygActivateGame,
  } = useGame({ gameId: gameId });
  const { navigate } = useAppNavigation();
  const dispatch = useDispatch();
  const { pending: linkedAccountsPending, accounts } = useLinkedAccounts();
  const layout = useLayout();

  const onSuccess = useCallback(async () => {
    onSuccessProp && onSuccessProp();

    if (origin === "addBonus") {
      await addToBonusGame();
      navigate(`/game/${gameId}`, { replace: true });
      addNotification(
        {
          type: NotificationType.SUCCESS,
          time: new Date().getTime(),
          message: "notifications.addBonusGame.success",
        },
        8000,
      )(dispatch);
    } else if (origin === "pickYourGame") {
      await pygActivateGame();
      navigate(`/game/${gameId}`, { replace: true });
    } else {
      addNotification(
        {
          type: NotificationType.SUCCESS,
          time: new Date().getTime(),
          message: "notifications.ubisoftconnect.success",
        },
        8000,
      )(dispatch);
      navigate(`/game/${gameId}`, { replace: true });
    }
  }, [
    addToBonusGame,
    dispatch,
    gameId,
    navigate,
    onSuccessProp,
    origin,
    pygActivateGame,
  ]);

  useEffect(() => {
    const init = async () => {
      if (!linkedAccountsPending) {
        //Check if already linked, if so proceed with activation
        const e = accounts.find((e) => e.kind === provider);
        logD(LOGGING_TAG, "Found linked account %o", e);
        if (e) {
          logD(LOGGING_TAG, "Already linked. Proceed with activation");
          await onSuccess();
        } else {
          const url = await createWebAppURL(`/oauth/${LinkedAccountType.UBISOFT}`, {
            gameid: gameId,
          });
          if (layout != AppLayout.TV) {
            window.open(url.toString(), "_blank");
          }
          setUrl(url.toString());
        }
      }
    };
    init();
  }, [
    accounts,
    createWebAppURL,
    gameId,
    layout,
    linkedAccountsPending,
    onSuccess,
    provider,
  ]);

  useEffect(() => {
    const sub = notificationService.onMessage().subscribe((n: NotificationMessage) => {
      if (n.kind !== NotificationMessageKind.NOTIFICATION_RESP) return;
      const { notification } = n.payload as any;
      if (notification.trigger === RemoteNotificationTrigger.UBISOFT_CONNECT) {
        logD(LOGGING_TAG, "Received ubisoft connect notification: %o", notification);
        if (notification.status === "success") {
          onSuccess();
        }
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, [onSuccess]);

  useErrorHandling({
    error: ubisoftPYGActivateError,
    clearError: dismissUbisoftPYGActivateError,
  });

  if (url) {
    logD(LOGGING_TAG, "Using ubisoft authorize URL: %o", url);
  }

  return { url };
};
